import { ReactElement, useEffect, useRef } from 'react';
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { Styles } from 'common/types/styles';
import { theme } from 'common/theme/theme';
import { PDPAttributeHistoryV2Item } from 'common/services/api/content/content-api.types';
import { PdpHistoryItemV2 } from './pdp-history-item-v2';

const styles: Styles = {
  tableContainer: { width: '100%', maxHeight: '500px', position: 'relative' },
  noItems: {
    fontSize: 12,
    color: theme.palette.grey[700],
    margin: '100px auto',
    display: 'block',
    width: 'max-content',
  },
  cell: { minWidth: '150px', whiteSpace: 'nowrap' },
  loaderWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    height: '200px',
    gap: 2,
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    zIndex: 10,
    gap: 2,
  },
  blurredTable: {
    filter: 'blur(2px)',
    pointerEvents: 'none',
  },
};

interface Props {
  history: Array<PDPAttributeHistoryV2Item> | null;
  isHistoryLoading?: boolean;
  total?: number;
  page?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
}

export function PdpHistoryContentV2({
  history,
  isHistoryLoading,
  total,
  page,
  pageSize,
  onPageChange,
}: Props): ReactElement {
  const hasPagination = total !== undefined && page !== undefined && pageSize !== undefined && onPageChange;
  const hasData = Boolean(history?.length);
  const isInitialLoading = isHistoryLoading && !hasData;
  const isPaginationLoading = isHistoryLoading && hasData;
  const tableContainerRef = useRef<HTMLDivElement>(null);

  // Scroll to top when page changes
  useEffect(() => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTop = 0;
    }
  }, [page]);

  if (isInitialLoading) {
    return (
      <Box sx={styles.loaderWrapper}>
        <CircularProgress size={14} />
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <>
      <TableContainer ref={tableContainerRef} sx={styles.tableContainer}>
        {hasData && (
          <>
            {isPaginationLoading && (
              <Box sx={styles.loadingOverlay}>
                <CircularProgress size={14} />
                <Typography>Loading...</Typography>
              </Box>
            )}
            <Table stickyHeader sx={isPaginationLoading ? styles.blurredTable : undefined}>
              <TableHead>
                <TableRow>
                  <TableCell sx={styles.cell}>Date</TableCell>
                  <TableCell sx={styles.cell}>Attribute Key</TableCell>
                  <TableCell sx={styles.cell}>Category</TableCell>
                  <TableCell sx={styles.cell}>Model Name</TableCell>
                  <TableCell sx={styles.cell}>Cost</TableCell>
                  <TableCell sx={styles.cell}>Process</TableCell>
                  <TableCell sx={styles.cell}>Generation Type</TableCell>
                  <TableCell sx={styles.cell}>Value</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history?.map((item, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <PdpHistoryItemV2 key={i} h={item} />
                ))}
              </TableBody>
            </Table>
          </>
        )}
        {!hasData && <Typography sx={styles.noItems}>No history</Typography>}
      </TableContainer>
      {hasPagination && total > 0 && (
        <TablePagination
          component="div"
          count={total}
          page={page}
          rowsPerPage={pageSize}
          rowsPerPageOptions={[pageSize]}
          onPageChange={(_, newPage) => onPageChange(newPage)}
        />
      )}
    </>
  );
}
